import React, { useState, useEffect } from 'react'
import axios from 'axios'
import * as XLSX from 'xlsx'

import { Input, Button } from 'reactstrap'

// Import Event Management components
import UpdateRegistration from '../components/EventManagement/UpdateRegistration'
import RegistrationTable from '../components/EventManagement/RegistrationTable'
import QrScanner from '../components/EventManagement/QRScanner'

// Import API config object
import { tokenConfig } from '../utils/apiConfig'




const EventManagement = props => {
    const statusHeaders = ['Registered', 'Checked In', 'Cancelled']
    const userDetails = props.userDetails
    const [statusFilter, setStatusFilter] = useState(statusHeaders.map((e, index) => {
        return { name: e, filterState: true, color: '#b3b3b3', index }
    }))
    const [events, setEvents] = useState([])
    const [allSessions, setAllSessions] = useState([])
    const [sessions, setSessions] = useState([])
    const [filteredRegistrations, setFilteredRegistrations] = useState([])
    const [updateRegistration, setUpdateRegistration] = useState(false)
    const [registration, setRegistration] = useState('')
    const [eventId, setEventId] = useState('')
    const [sessionId, setSessionId] = useState('')
    const [textFilter, setTextFilter] = useState('')

    useEffect(() => {
        getEvents()
    }, [userDetails])

    useEffect(() => {
        if (sessionId) {
            filterRegistrations()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, sessionId])

    useEffect(() => {
        setFilteredRegistrations([])
        setSessions([])
        if (eventId) {
            getEventSessions(eventId)
        }
    }, [eventId])

    const yesterday = new Date()
    yesterday.setHours(0, 0, 0, 0)
    yesterday.setDate(yesterday.getDate() - 1)
    const fifteenDaysLater = new Date()
    fifteenDaysLater.setHours(23, 59, 59, 999)
    fifteenDaysLater.setDate(fifteenDaysLater.getDate() + 15)

    const query = textFilter.trim().toLowerCase()
    const displayedRegistrations = query
        ? filteredRegistrations.filter(r =>
            [r.email, r.name, r.validId, r.phoneNumber].some(
                field => field && field.toLowerCase().includes(query)
            )
        )
        : filteredRegistrations

    const eventIdsWithSessions = new Set(allSessions.map(s => String(s.eventId)))
    const eventsWithSessions = events.filter(e => eventIdsWithSessions.has(String(e._id)))
    const upcomingSessions = allSessions
        .filter(s => {
            const d = new Date(s.date)
            return d >= yesterday && d <= fifteenDaysLater
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))

    const filterRegistrations = () => {
        var filters = {}
        filters.status = statusFilter.filter(e => e.filterState).map(e => e.name)
        axios.post('/api/registrations/filter', { filter: { sessionId } }, tokenConfig())
            .then(res => {
                const arr = res.data.filter(registration => {
                    return Object.keys(filters).every(key => {
                        if (Array.isArray(filters[key])) {
                            return filters[key].includes(registration[key])
                        }
                        return filters[key] === registration[key]
                    })
                })
                setFilteredRegistrations(arr)
            })
            .catch(err => console.log(err))
    }

    const getEventSessions = async (id) => {
        await axios.post('/api/event-sessions/getSessionsByEventId', { eventId: id, filters: {} }, tokenConfig())
            .then(res => setSessions(res.data))
            .catch(err => console.log(err))
    }

    const changeFilterState = (index) => {
        const arr = statusFilter.map((e, i) => {
            if (i === index) {
                e.filterState = !e.filterState
                return e
            }
            return e
        })
        setStatusFilter(arr)
    }

    const getEvents = async () => {
        try {
            const [eventsRes, sessionsRes] = await Promise.all([
                axios.get('/api/events', tokenConfig()),
                axios.get('/api/event-sessions', tokenConfig())
            ])
            setEvents(eventsRes.data)
            setAllSessions(sessionsRes.data)
        } catch (err) {
            console.log(err)
        }
    }

    const cancelRegistration = async _id => {
        const confirm = window.confirm("Cancel your registration?")
        if (!confirm) return console.log("Cancelled Request")
        await axios.delete(`/api/registrations/${_id}`, tokenConfig())
            .then(() => filterRegistrations())
            .catch(err => console.log(err))
    }

    const selectedSession = sessions.find(s => s._id === sessionId)
    const selectedEvent = events.find(e => e._id === eventId)

    const downloadExcel = () => {
        const rows = []
        displayedRegistrations.forEach(reg => {
            rows.push({
                Type: 'Registrant',
                Name: reg.name,
                Email: reg.email,
                ID: reg.validId,
                Phone: reg.phoneNumber,
                DOB: reg.dob ? new Date(reg.dob).toLocaleDateString() : '',
                Status: reg.status,
            })
            reg.guests?.forEach(guest => {
                rows.push({
                    Type: 'Guest',
                    Name: guest.name,
                    Email: reg.email,
                    ID: guest.validId,
                    Phone: '',
                    DOB: guest.dob ? new Date(guest.dob).toLocaleDateString() : '',
                    Status: reg.status,
                })
            })
        })
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Attendees')
        const date = selectedSession?.date ? new Date(selectedSession.date).toISOString().substring(0, 10) : 'export'
        const name = selectedEvent?.name?.replace(/\s+/g, '_') || 'attendees'
        XLSX.writeFile(wb, `${name}_${date}.xlsx`)
    }

    const selectUpcomingSession = (session) => {
        setEventId(String(session.eventId))
        setSessionId(session._id)
    }


    return (
        <div className='page-shell page-shell--wide page-section'>
            <div className='page-header'>
                <div>
                    <span className='eyebrow'>Event Operations</span>
                    <h1 className='section-title'>Manage registrations and check-ins</h1>
                    <p className='section-lead'>Filter by event or session, review attendees, and update status.</p>
                </div>
            </div>

            {upcomingSessions.length > 0 && (
                <div className='fobk-surface-card upcoming-session-filters'>
                    <p className='eyebrow' style={{ marginBottom: '0.75rem' }}>Upcoming</p>
                    <div className='session-quick-pills'>
                        {upcomingSessions.map(session => {
                            const event = events.find(e => String(e._id) === String(session.eventId))
                            return (
                                <button
                                    key={session._id}
                                    type='button'
                                    className={`session-quick-pill${sessionId === session._id ? ' is-active' : ''}`}
                                    onClick={() => selectUpcomingSession(session)}
                                >
                                    <span className='session-quick-pill__event'>{event?.name}</span>
                                    <span className='session-quick-pill__date'>
                                        {new Date(session.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}&nbsp;&nbsp;{session.starttime}–{session.endtime}&nbsp;&nbsp;({session.registrations ?? 0}/{event?.capacity ?? '—'})
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}

            <div className='fobk-surface-card management-controls'>
                <div className='management-controls__grid'>
                    <div className='management-selects'>
                        <Input type="select" value={eventId} onChange={e => { setEventId(e.target.value); setSessionId('') }}>
                            <option value="">Select Event</option>
                            {eventsWithSessions.sort((a, b) => a.name < b.name ? -1 : 1).map(({ _id, name }) => <option value={_id} key={_id}>{name}</option>)}
                        </Input>
                        <Input type="select" value={sessionId} onChange={e => setSessionId(e.target.value)}>
                            <option value="">Select Session</option>
                            {sessions.map(({ _id, date, starttime, endtime }) => <option value={_id} key={_id}>{date?.substr(0, 10)}, {starttime}-{endtime}</option>)}
                        </Input>
                    </div>

                    <div className='filter-pills'>
                        {statusFilter.map(({ name, filterState, index }) => (
                            <button
                                className={`filter-pill ${filterState ? '' : 'is-inactive'}`}
                                key={name}
                                type='button'
                                onClick={() => changeFilterState(index)}
                            >
                                {name}
                            </button>
                        ))}
                    </div>

                    <div className='management-actions'>
                        <QrScanner updateStatus={filterRegistrations} />
                        <Button
                            onClick={filterRegistrations}
                            disabled={!sessionId}
                        >
                            Reload Data
                        </Button>
                        <Button
                            onClick={downloadExcel}
                            disabled={displayedRegistrations.length === 0}
                            color='success'
                        >
                            Export Excel
                        </Button>
                    </div>
                </div>
                {eventId && sessionId && selectedSession && (
                    <div className='session-info-strip'>
                        <div className='session-info-strip__item'>
                            <span>Capacity</span>
                            <strong>{selectedEvent?.capacity ?? '—'}</strong>
                        </div>
                        <div className='session-info-strip__item'>
                            <span>Registrations</span>
                            <strong>{selectedSession.registrations ?? 0}</strong>
                        </div>
                        <div className='session-info-strip__item'>
                            <span>Status</span>
                            <strong>{selectedSession.status}</strong>
                        </div>
                    </div>
                )}
                {filteredRegistrations.length > 0 && (
                    <div className='text-filter-row'>
                        <Input
                            type='text'
                            placeholder='Search by name, email, ID or phone...'
                            value={textFilter}
                            onChange={e => setTextFilter(e.target.value)}
                        />
                        {textFilter && (
                            <button type='button' className='text-filter-clear' onClick={() => setTextFilter('')}>×</button>
                        )}
                    </div>
                )}
            </div>

            <UpdateRegistration
                toggle={e => setUpdateRegistration(e)}
                updateRegistration={updateRegistration}
                registration={registration}
                insertUpdatedRegistration={filterRegistrations}
            />
            <div className='fobk-surface-card table-shell'>
                <RegistrationTable
                    events={events}
                    registrations={displayedRegistrations}
                    setUpdateRegistration={setUpdateRegistration}
                    cancelRegistration={cancelRegistration}
                    setRegistration={setRegistration}
                />
            </div>
        </div>
    )
}

export default EventManagement
